import {
  ModalBlockDeleteReason,
  ModalChangePassword,
} from "components/molecules";
import { RestoreUsersAction, UnblockUsersAction } from "store/middlewares/user";
import { Menu, MenuItem } from "@mui/material";
import { MuiBox, MuiTypography } from "components/atoms";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { openAdminModal, closeAdminModal } from "store/slices/common/adminModal.slice";
import { Button } from "components/ui";
import {
  ArchiveRestore,
  EllipsisVertical,
  Info,
  Lock,
  RotateCw,
  ShieldBan,
  Trash,
} from "lucide-react";

import checkAccess from "utils/checkAccess";

const UserListActionBar = ({ row, filter }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Block / Delete: ONE modal only (no Swal confirmation before it)
  const openBlockDeleteModal = (type) => {
    dispatch(
      openAdminModal({
        title: "",
        content: (
          <ModalBlockDeleteReason id={row.id} type={type} filter={filter} />
        ),
      }),
    );
  };

  //--------------------------------------------------- Action Menu Array --------------------------------------
  const ActionMenuArray = [
    checkAccess("read_user_detail") && {
      label: "Detail",
      icon: <Info size={16} color="#475569" />,
      onClick: () => {
        navigate(`/users/detail/${row.id}`);
      },
    },
    row?.status === "active" &&
      checkAccess("update_users") && {
        label: "Block",
        icon: <ShieldBan size={16} color="#475569" />,
        onClick: () => openBlockDeleteModal("blocked"),
      },
    checkAccess("update_users") &&
      row?.status === "deleted" && {
        label: "Restore",
        icon: <ArchiveRestore size={16} color="#475569" />,
        onClick: () => {
          dispatch(
            openAdminModal({
              title: "",
              content: (
                <div className="flex flex-col gap-6 p-2 sm:p-4">
                  {/* Header */}
                  <div className="flex items-center gap-4">
                    <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                      <ArchiveRestore className="size-6" strokeWidth={2.5} />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      Restore this user?
                    </h2>
                  </div>

                  {/* Bullet List */}
                  <ul className="space-y-3.5 pl-1 text-[15px] text-gray-600">
                    <li className="flex items-center gap-3">
                      <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-gray-400" />
                      They will be able to sign in again right away.
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-gray-400" />
                      They can access Mail, Chat, and Drive.
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-gray-400" />
                      Their previous data and settings will remain intact.
                    </li>
                  </ul>

                  {/* Buttons */}
                  <div className="mt-2 flex justify-end gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="lg"
                      onClick={() => dispatch(closeAdminModal())}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      size="lg"
                      onClick={() => {
                        dispatch(RestoreUsersAction({ id: row.id, filter }));
                        dispatch(closeAdminModal());
                      }}
                    >
                      Restore user
                    </Button>
                  </div>
                </div>
              ),
            }),
          );
        },
      },
    checkAccess("update_users") &&
      row?.status === "blocked" && {
        label: "Unblock",
        icon: <RotateCw size={16} color="#475569" />,
        onClick: () => {
          dispatch(
            openAdminModal({
              title: "",
              content: (
                <div className="flex flex-col gap-6 p-2 sm:p-4">
                  {/* Header */}
                  <div className="flex items-center gap-4">
                    <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                      <RotateCw className="size-6" strokeWidth={2.5} />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      Unblock this user?
                    </h2>
                  </div>

                  {/* Bullet List */}
                  <ul className="space-y-3.5 pl-1 text-[15px] text-gray-600">
                    <li className="flex items-center gap-3">
                      <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-gray-400" />
                      They can sign in to Mail, Chat, and Drive immediately.
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-gray-400" />
                      They will regain access to all their previous data.
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-gray-400" />
                      You can block them again at any time.
                    </li>
                  </ul>

                  {/* Buttons */}
                  <div className="mt-2 flex justify-end gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="lg"
                      onClick={() => dispatch(closeAdminModal())}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      size="lg"
                      onClick={() => {
                        dispatch(UnblockUsersAction({ id: row.id, filter }));
                        dispatch(closeAdminModal());
                      }}
                    >
                      Unblock user
                    </Button>
                  </div>
                </div>
              ),
            }),
          );
        },
      },
    checkAccess("delete_users") &&
      row?.status === "active" && {
        label: "Delete",
        icon: <Trash size={16} color="#475569" />,
        onClick: () => openBlockDeleteModal("delete"),
      },
    checkAccess("update_users") &&
      row?.status === "active" && {
        label: "Change Password",
        icon: <Lock size={16} color="#475569" />,
        onClick: () =>
          dispatch(
            openAdminModal({
              title: "",
              content: <ModalChangePassword row={row} />,
            }),
          ),
      },
  ].filter(Boolean);

  // For action menu
  const [actionMenu, setActionMenu] = useState(null);
  const handleActionMenuOpen = (event) => {
    setActionMenu(event.currentTarget);
  };
  const handleActionMenuClose = () => {
    setActionMenu(null);
  };

  return (
    <MuiBox className="flex h-full items-center justify-center gap-3 ">
      <Button
        className="size-7"
        mode="icon"
        variant="ghost"
        onClick={handleActionMenuOpen}
      >
        <EllipsisVertical />
      </Button>

      <Menu
        anchorEl={actionMenu}
        open={Boolean(actionMenu)}
        onClose={handleActionMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          " & .MuiMenu-paper": {
            mt: "10px",
            background: "#fff",
            boxShadow:
              " 0 4px 6px -1px #e2e8f0, 0 2px 4px -2px #e2e8f0 !important",
          },
        }}
      >
        {ActionMenuArray.map((option) => (
          <MenuItem
            key={option.label}
            onClick={() => {
              handleActionMenuClose();
              option.onClick();
            }}
          >
            <MuiBox
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "9px",
              }}
            >
              <MuiBox sx={{ width: "fit-content" }}>{option.icon}</MuiBox>
              <MuiTypography
                sx={{ fontWeight: "500", fontSize: "13px", color: "#475569" }}
              >
                {option.label}
              </MuiTypography>
            </MuiBox>
          </MenuItem>
        ))}
      </Menu>
    </MuiBox>
  );
};

export default UserListActionBar;